import { isAxiosError } from "axios";
import { create } from "zustand";
import { ClientApp } from "target_wasm";
import { axiosInstance } from "contexts/axiosApiInstance";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import { setImportQueueModel } from "@/components/canvas/fileUpload/importQueue";
import {
  createWebsocket,
  type DisconnectReason,
} from "@/components/canvas/utils/CanvasActions";
import { cleanup } from "@/components/canvas/utils/utils";

export type Refusal = "not_found" | "duplicate" | "signed_out" | "failed";
export type OpenResult = "ready" | "cancelled" | Refusal;

interface SocketHandlers {
  onConnected: () => void;
  onDisconnected: (reason: DisconnectReason) => void;
}

interface Session {
  modelId: string;
  clientId: string;
  clientApp: ClientApp;
  ready: boolean;
  result: Promise<OpenResult>;
  settle: (result: OpenResult) => void;
  disconnect: () => void;
  stopWatching: () => void;
  onDuplicate: () => void;
}

// One model is open per tab: the one being opened, then the one on the canvas.
let session: Session | null = null;

/** The model being opened, until the canvas shows it or it closes; its
 *  Overview row shows the spinner meanwhile. */
export const useOpeningModel = create<{ modelId: string | null }>(() => ({
  modelId: null,
}));

/** Loads the model with no canvas on the page; resolves once it is ready to
 *  show. Opening the model that is already open joins it. */
export function openModel(
  modelId: string,
  onDuplicate: () => void = () => {},
): Promise<OpenResult> {
  if (session?.modelId === modelId) {
    session.onDuplicate = onDuplicate;
    useOpeningModel.setState({ modelId: null });
    return session.result;
  }
  closeModel();

  const clientId = localStorage.getItem("clientID");
  if (!clientId) return Promise.resolve("signed_out");

  const store = useBoundStore.getState();
  store.initializeWorker();
  const clientApp = ClientApp.new(clientId, modelId);
  store.setClientApp(clientApp);
  // A held import batch needs this worker and this client to run.
  setImportQueueModel(modelId);

  const { promise: result, resolve: settle } =
    Promise.withResolvers<OpenResult>();
  const s: Session = {
    modelId,
    clientId,
    clientApp,
    ready: false,
    result,
    settle,
    disconnect: () => {},
    stopWatching: () => {},
    onDuplicate,
  };
  session = s;
  useOpeningModel.setState({ modelId });

  // The store updates from inside wasm calls, where the client is still
  // borrowed; asking it anything before the call returns throws.
  s.stopWatching = useBoundStore.subscribe(() =>
    queueMicrotask(() => {
      if (
        session !== s ||
        s.ready ||
        !s.clientApp.is_model_ready() ||
        useBoundStore.getState().websocket?.readyState !== WebSocket.OPEN
      )
        return;
      s.ready = true;
      s.stopWatching();
      s.settle("ready");
    }),
  );
  s.disconnect = connect(s, {
    onConnected: () => {},
    onDisconnected: (reason) => {
      if (session !== s) return;
      if (s.ready) {
        if (reason === "duplicate") s.onDuplicate();
        return;
      }
      // A browser hides why an upgrade failed; the server says it here.
      const refusal =
        reason === "duplicate"
          ? Promise.resolve<Refusal>("duplicate")
          : explainRefusal(modelId, clientId);
      void refusal.then((r) => {
        if (session !== s) return;
        s.settle(r);
        closeModel();
      });
    },
  });
  return result;
}

/** A new socket for the open model; the reconnect engine's attempts land here. */
export function reconnectModel(handlers: SocketHandlers): boolean {
  if (!session) return false;
  session.disconnect();
  session.disconnect = connect(session, handlers);
  return true;
}

/** Closes the model if it is still being opened, e.g. its row was cancelled. */
export function cancelOpening(modelId: string) {
  if (session?.modelId === modelId && !session.ready) closeModel();
}

export function closeModel() {
  const s = session;
  if (!s) return;
  session = null;
  useOpeningModel.setState({ modelId: null });
  s.stopWatching();
  s.disconnect();
  s.settle("cancelled");
  const store = useBoundStore.getState();
  store.resetPresence();
  // `resetClientApp` clears the canvas mode and every overlay the freed
  // ClientApp pushed — they're per-model and would otherwise stick.
  cleanup(() => store.resetClientApp(), "Reset client app");
  // Before the worker goes: a row that starts between the two would post
  // to a worker about to be terminated.
  setImportQueueModel(null);
  cleanup(() => store.terminateWorker(), "Terminate worker");
}

export function notifyRefusal(refusal: Refusal) {
  if (refusal === "not_found") {
    showNotificationGlobal("Model was not found!", "error");
  } else if (refusal === "duplicate") {
    showNotificationGlobal(
      "This model is already active in a different tab or window!",
    );
  } else if (refusal === "failed") {
    showNotificationGlobal("Couldn't open the model. Try again.", "error");
  }
  // signed_out: the session-expired dialog already says so.
}

function connect(s: Session, handlers: SocketHandlers) {
  return createWebsocket({
    clientId: s.clientId,
    clientApp: s.clientApp,
    modelId: s.modelId,
    onSocketChange: useBoundStore.getState().setWebsocket,
    ...handlers,
  });
}

async function explainRefusal(
  modelId: string,
  clientId: string,
): Promise<Refusal> {
  const [access, connectable] = await Promise.allSettled([
    axiosInstance.post(`/action/check_access/${modelId}`),
    axiosInstance.get(`/action/can_connect/${modelId}/${clientId}`),
  ]);
  const status = (r: PromiseSettledResult<unknown>) =>
    r.status === "rejected" && isAxiosError(r.reason)
      ? r.reason.response?.status
      : undefined;
  if (status(access) === 401 || status(connectable) === 401) {
    return "signed_out";
  }
  if (status(access) === 403 || status(access) === 404) return "not_found";
  if (status(connectable) === 409) return "duplicate";
  return "failed";
}
